import {
    MdAccessTime,
    MdAnalytics,
    MdAnnouncement,
    MdAttachMoney,
    MdCalendarToday,
    MdCelebration,
    MdDashboard,
    MdDescription,
    MdDocumentScanner,
    MdGavel,
    MdHandshake,
    MdManageAccounts,
    MdMessage,
    MdMyLocation,
    MdPeople,
    MdReceipt,
    MdReceiptLong,
    MdSavings,
    MdSettings,
    MdTransferWithinAStation,
} from 'react-icons/md';

// Route paths mirror the permission groups used in SidebarOld.js so that the
// role/user permission checks keep gating exactly the same menu entries.
//
// Two flags drive the role-specific behaviour that SidebarOld.js expressed by
// duplicating every menu into a `role === 'Super Admin'` branch and a
// permission branch:
//   superAdminOnly    - only ever rendered in the Super Admin branch
//   hideForSuperAdmin - commented out in the Super Admin branch, or explicitly
//                       wrapped in `role !== 'Super Admin'`
//
// Menus that SidebarOld.js commented out, or that only ever existed as dead
// `submenuControl` state keys with no JSX behind them, are intentionally absent.
const employeeRouterPath = [
    'api/employee/basic',
    'api/employee',
    'api/employee/basic-info/import',
];
const docRouterPath = ['api/employee-internal-document', 'api/employee-internal-document/self'];
const attendanceRouterPath = [
    'api/attendance/self',
    'api/attendance/summary',
    'api/attendance/daily',
    'api/attendance/details',
    'api/attendance-summery/by-supervisor',
    'api/attendance-daily/by-supervisor',
    'api/attendance/bulk/upload',
];
const leaveRouterPath = [
    'api/leave/self',
    'api/leave',
    'api/leave/create',
    'api/leave/all-by-supervisor',
];
const providentFundPath = [
    'api/provident-fund/self',
    'api/leavprovident-funde',
    'api/provident-fund/create',
    'api/provident-fund/all-by-supervisor',
];
const loanRequestPath = ['api/loan-request', 'api/type-of-loan', 'api/loan-approval-setting'];
const gratuityPath = [
    'api/gratuity/self',
    'api/leavgratuitye',
    'api/gratuity/create',
    'api/gratuity/all-by-supervisor',
];
const payrollRouterPath = [
    'api/payroll',
    'api/salary/sheet/generate/single',
    'api/salary/sheet/generate',
    'api/payroll/self',
    'api/payroll/all-by-supervisor',
    'api/salary/sheet/bank',
];
const announcementRouterPath = [
    'api/announcement/self',
    'api/announcement',
    'api/announcement/create',
];
const transferRouterPath = ['api/transfer/self', 'api/transfer'];
const settlementRouterPath = ['api/settlement', 'api/settlement/create'];
const settlementRequestRouterPath = ['api/settlement-request', 'api/settlement-request/create'];
const mrExpenseRouterPath = ['api/market-expense', 'api/market-expense/create'];
const taxCertificateRouterPath = [
    'api/tax-certificate/create',
    'api/tax-certificate',
    'api/tax-certificate/tdx/employee',
];
const messageRouterPath = ['api/message/self', 'api/message', 'api/message/create'];
const reportRouterPath = [
    'api/report/employee',
    'api/report/attendance/daily',
    'api/report/attendance/summery',
    'api/report/leave',
    'api/report/payroll',
    'api/report/sattlement',
];

export const NAV_ITEMS = [
    { icon: MdDashboard, label: 'Dashboard', path: '/dashboard/home' },
    {
        icon: MdPeople,
        label: 'Employees',
        permissionPaths: employeeRouterPath,
        subItems: [
            { label: 'Add Employees', path: '/employees/add', permissionPaths: [employeeRouterPath[0]] },
            {
                label: 'Add Employees (Bulk)',
                path: '/employees/bulk-add',
                permissionPaths: [employeeRouterPath[2]],
            },
            {
                label: 'Manage Employees',
                path: '/employees/manage',
                permissionPaths: [employeeRouterPath[1]],
            },
        ],
    },
    {
        icon: MdDocumentScanner,
        label: 'Legal Docs',
        permissionPaths: docRouterPath,
        subItems: [
            // SidebarOld.js gates these two on employeeRouterPath rather than
            // docRouterPath; kept as-is to preserve the existing behaviour.
            { label: 'Manage Docs', path: '/employee-docs/manage', permissionPaths: [employeeRouterPath[0]] },
            { label: 'Manage My Docs', path: '/employee-docs/my-docs', permissionPaths: [docRouterPath[1]] },
            {
                label: 'Generate Docs',
                path: '/employee-docs/generate',
                permissionPaths: [employeeRouterPath[2]],
            },
        ],
    },
    {
        icon: MdAccessTime,
        label: 'Attendance',
        permissionPaths: attendanceRouterPath,
        subItems: [
            {
                label: 'My Daily',
                path: '/attendance/my/daily',
                permissionPaths: [attendanceRouterPath[0]],
                hideForSuperAdmin: true,
            },
            {
                label: 'My Summery',
                path: '/attendance/my/summary',
                permissionPaths: [attendanceRouterPath[3]],
                hideForSuperAdmin: true,
            },
            { label: 'Daily', path: '/attendance/daily', permissionPaths: [attendanceRouterPath[2]] },
            { label: 'Summary', path: '/attendance/summary', permissionPaths: [attendanceRouterPath[1]] },
            {
                label: 'Summary By Supervisor',
                path: '/attendance/summary-by-supervisor',
                permissionPaths: [attendanceRouterPath[4]],
                hideForSuperAdmin: true,
            },
            {
                label: 'Daily By Supervisor',
                path: '/attendance/daily-by-supervisor',
                permissionPaths: [attendanceRouterPath[5]],
                hideForSuperAdmin: true,
            },
            { label: 'Bulk Attendance', path: '/attendance/bulk', permissionPaths: [attendanceRouterPath[6]] },
        ],
    },
    {
        icon: MdMyLocation,
        label: 'Tracking',
        path: '/tracking/map',
        permissionPaths: ['api/employee-location'],
    },
    {
        icon: MdCalendarToday,
        label: 'Leave',
        permissionPaths: leaveRouterPath,
        subItems: [
            {
                label: 'My Leave',
                path: '/leave/self',
                permissionPaths: [leaveRouterPath[0]],
                hideForSuperAdmin: true,
            },
            { label: 'Manage Leave', path: '/leave/manage', permissionPaths: [leaveRouterPath[1]] },
            {
                label: 'Manage Leave By Supervisor',
                path: '/leave/manage-by-supervisor',
                permissionPaths: [leaveRouterPath[3]],
                hideForSuperAdmin: true,
            },
            {
                label: 'New Request',
                path: '/leave/add',
                permissionPaths: [leaveRouterPath[2]],
                hideForSuperAdmin: true,
            },
            // Only present in the Super Admin branch of SidebarOld.js.
            { label: 'Leave Encashment', path: '/encashment/manage', superAdminOnly: true },
        ],
    },
    {
        icon: MdSavings,
        label: 'Provident Fund',
        permissionPaths: providentFundPath,
        subItems: [
            { label: 'Manage Fund', path: '/provident-fund/manage' },
            { label: 'PF Settings', path: '/provident-fund/settings' },
        ],
    },
    {
        icon: MdHandshake,
        label: 'Loan Management',
        permissionPaths: loanRequestPath,
        subItems: [
            { label: 'Loan Manage', path: '/loan-manage/list', permissionPaths: [loanRequestPath[0]] },
            { label: 'Loan Type', path: '/loan-manage/type', permissionPaths: [loanRequestPath[1]] },
            {
                label: 'Loan Approval Settings',
                path: '/loan-manage/approval',
                permissionPaths: [loanRequestPath[2]],
            },
        ],
    },
    {
        icon: MdGavel,
        label: 'Gratuity',
        permissionPaths: gratuityPath,
        subItems: [
            { label: 'Manage Gratuity', path: '/gratuity/manage' },
            { label: 'Gratuity Settings', path: '/gratuity/settings' },
        ],
    },
    {
        icon: MdCelebration,
        label: 'Holiday',
        path: '/holiday/list',
        permissionPaths: ['api/holiday'],
    },
    {
        icon: MdAttachMoney,
        label: 'Payroll',
        permissionPaths: payrollRouterPath,
        subItems: [
            {
                label: 'My Payroll',
                path: '/payroll/self',
                permissionPaths: [payrollRouterPath[3]],
                hideForSuperAdmin: true,
            },
            { label: 'Manage Payroll', path: '/payroll/manage', permissionPaths: [payrollRouterPath[0]] },
            {
                label: 'Manage Payroll By Supervisor',
                path: '/payroll/manage-by-supervisor',
                permissionPaths: [payrollRouterPath[4]],
                hideForSuperAdmin: true,
            },
            // SidebarOld.js sends Super Admin to /payroll/manage-generation and
            // permission users to /payroll/all-generate.
            {
                label: 'Generate Payroll',
                path: '/payroll/manage-generation',
                superAdminOnly: true,
            },
            {
                label: 'Generate Payroll',
                path: '/payroll/all-generate',
                permissionPaths: [payrollRouterPath[2]],
                hideForSuperAdmin: true,
            },
            { label: 'Bank Advice', path: '/payroll/bank-advice', permissionPaths: [payrollRouterPath[5]] },
        ],
    },
    {
        icon: MdAnnouncement,
        label: 'Announcement',
        permissionPaths: announcementRouterPath,
        subItems: [
            {
                label: 'My Announcement',
                path: '/announcement/self',
                permissionPaths: [announcementRouterPath[0]],
            },
            {
                label: 'Announcement List',
                path: '/announcement/list',
                permissionPaths: [announcementRouterPath[1]],
            },
        ],
    },
    {
        icon: MdTransferWithinAStation,
        label: 'Transfer',
        path: '/transfer/list',
        permissionPaths: [transferRouterPath[1]],
    },
    {
        icon: MdHandshake,
        label: 'Settlement Requests',
        subItems: [
            {
                label: 'Request Settlement',
                path: '/settlements/request',
                permissionPaths: [settlementRequestRouterPath[1]],
                hideForSuperAdmin: true,
            },
            {
                label: 'Settlement Requests',
                path: '/settlements/requests',
                permissionPaths: [settlementRequestRouterPath[0]],
            },
        ],
    },
    {
        icon: MdReceipt,
        label: 'MR Expense',
        permissionPaths: mrExpenseRouterPath,
        subItems: [
            {
                label: 'Create MR Expense',
                path: '/mr-expense/create',
                permissionPaths: [mrExpenseRouterPath[1]],
                hideForSuperAdmin: true,
            },
            { label: 'MR Expense List', path: '/mr-expense/list', permissionPaths: [mrExpenseRouterPath[0]] },
        ],
    },
    {
        icon: MdReceiptLong,
        label: 'Settlement',
        permissionPaths: settlementRouterPath,
        subItems: [
            { label: 'New Settlement', path: '/settlements/new', permissionPaths: [settlementRouterPath[1]] },
            { label: 'Settled List', path: '/settlements/list', permissionPaths: [settlementRouterPath[0]] },
        ],
    },
    {
        icon: MdDescription,
        label: 'Tax Certificate',
        permissionPaths: taxCertificateRouterPath,
        subItems: [
            {
                label: 'Generate Tax Certificate',
                path: '/tax-certificate/generate',
                permissionPaths: [taxCertificateRouterPath[0]],
            },
            {
                label: 'List Tax Certificate',
                path: '/tax-certificate/list',
                permissionPaths: [taxCertificateRouterPath[1]],
            },
            {
                label: 'Fiscal Year Tax Certificate',
                path: '/tax-certificate/fiscal',
                permissionPaths: [taxCertificateRouterPath[2]],
            },
        ],
    },
    {
        icon: MdMessage,
        label: 'Message',
        permissionPaths: messageRouterPath,
        subItems: [
            {
                label: 'Inbox',
                path: '/message/inbox',
                permissionPaths: [messageRouterPath[0], messageRouterPath[1]],
            },
            // Only present in the Super Admin branch of SidebarOld.js.
            { label: 'All Messages', path: '/message/all', superAdminOnly: true },
            {
                label: 'Sent',
                path: '/message/send',
                permissionPaths: [messageRouterPath[0], messageRouterPath[1]],
            },
            { label: 'Compose', path: '/message/compose-list', permissionPaths: [messageRouterPath[2]] },
        ],
    },
    {
        icon: MdAnalytics,
        label: 'Reports',
        permissionPaths: reportRouterPath,
        subItems: [
            { label: 'Employee Report', path: '/reports/employee', permissionPaths: [reportRouterPath[0]] },
            {
                label: 'Attendance Daily Report',
                path: '/reports/attendance/daily',
                permissionPaths: [reportRouterPath[1]],
            },
            {
                label: 'Attendance Summery Report',
                path: '/reports/attendance/summery',
                permissionPaths: [reportRouterPath[2]],
            },
            { label: 'Leave Report', path: '/reports/leave', permissionPaths: [reportRouterPath[3]] },
            { label: 'Payroll Report', path: '/reports/payroll', permissionPaths: [reportRouterPath[4]] },
            {
                label: 'Settlement Report',
                path: '/reports/settlement',
                permissionPaths: [reportRouterPath[5]],
            },
        ],
    },
    {
        icon: MdManageAccounts,
        label: 'Users',
        path: '/user/list',
        permissionPaths: ['api/role/user'],
    },
    {
        icon: MdSettings,
        label: 'Settings',
        // The permission-gated Settings branch is commented out in
        // SidebarOld.js, so Settings is Super Admin only.
        superAdminOnly: true,
        subItems: [
            {
                label: 'Configuration',
                isGroup: true,
                subItems: [
                    { label: 'App', path: '/settings/app-config' },
                    { label: 'General', path: '/settings/general-config' },
                    { label: 'Bank', path: '/settings/bank-config' },
                    { label: 'Logo', path: '/settings/logo-list' },
                    { label: 'Banner', path: '/settings/banner-list' },
                    { label: 'SMTP', path: '/settings/mail-config' },
                    { label: 'Branch', path: '/settings/branch' },
                    { label: 'Role', path: '/settings/role' },
                    { label: 'KAM', path: '/settings/manage-kam' },
                    { label: 'Challan Year', path: '/settings/challan-year' },
                    { label: 'Employee Grade', path: '/settings/employee-grade' },
                    { label: 'Attendance', path: '/settings/attendance' },
                    { label: 'Job Contract', path: '/settings/job-contact' },
                    { label: 'Office Shift', path: '/settings/office-shift-list' },
                    { label: 'Leave Type', path: '/settings/leave-type' },
                    { label: 'Document Type', path: '/settings/document-type' },
                    { label: 'Salary Type', path: '/settings/salary-type' },
                    { label: 'Department', path: '/settings/department-list' },
                    { label: 'Designation', path: '/settings/designation-list' },
                    { label: 'Award Type', path: '/settings/award-type' },
                    { label: 'Tax Category', path: '/settings/tax-category' },
                    { label: 'Tax Settings', path: '/settings/tax-settings' },
                    { label: 'Tax Investment Type', path: '/settings/tax-investment-type' },
                    { label: 'Vehicle Type', path: '/settings/vehicle-type' },
                    {
                        label: 'Lunch Allowance Settings',
                        path: '/settings/lunch-allowance-settings',
                    },
                    { label: 'LTA Allowance Settings', path: '/settings/lta-settings' },
                    {
                        label: 'Absence Deduction Settings',
                        path: '/settings/absence-deduction-settings',
                    },
                    {
                        label: 'PF Investment Interest Settings',
                        path: '/settings/Pf-interest-Setting',
                    },
                    { label: 'Max Loan PF Settings', path: '/settings/max-loan-pf-settings' },
                ],
            },
            {
                label: 'Permission',
                isGroup: true,
                subItems: [
                    { label: 'Role Wise Permission', path: '/settings/role-permissions' },
                    { label: 'User Wise Permission', path: '/settings/user-permissions' },
                ],
            },
        ],
    },
];
